import { useEffect, useRef, useCallback } from 'react';
import messagingService from 'services/messagingService';

/**
 * useMessagingWS
 * Manages a WebSocket connection for real-time messaging.
 *
 * @param {string} role - 'vendor' | 'customer'
 * @param {object} callbacks
 * @param {function} callbacks.onNewMessage        - Called with the message.new data payload
 * @param {function} callbacks.onTyping            - Called with the typing data payload
 * @param {function} callbacks.onReadReceipt       - Called with the read_receipt data payload
 * @param {function} callbacks.onUnreadUpdate      - Called with the unread_count_update data payload
 * @param {function} callbacks.onTrendReportStarted - Called when a trend_report_started event is received
 * @param {function} callbacks.onTrendReportReady  - Called when a trend_report_ready event is received
 * @param {function} callbacks.onTrendReportFailed - Called when a trend_report_failed event is received
 * @returns {{ sendTyping: function, wsRef: object }}
 */
const useMessagingWS = (role, {
  onNewMessage,
  onTyping,
  onReadReceipt,
  onUnreadUpdate,
  onTrendReportStarted,
  onTrendReportReady,
  onTrendReportFailed,
  // Business / data events (server pushes these when data changes)
  onNewSubscriber,
  onBatchUpdated,
  onServiceUpdated,
  onStaffUpdated,
} = {}) => {

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const isConnectingRef = useRef(false);

  const connect = useCallback(() => {
    // Prevent duplicate connections
    if (isConnectingRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const url = messagingService.getWebSocketURL(role);
    const wsUrl = `${url}?token=${token}`;

    isConnectingRef.current = true;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        // Clear any pending reconnect timer
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const { event: evtType, data } = JSON.parse(event.data);
          if (evtType === 'message.new' && onNewMessage) {
            onNewMessage(data);
          } else if (evtType === 'typing' && onTyping) {
            onTyping(data);
          } else if (evtType === 'read_receipt' && onReadReceipt) {
            onReadReceipt(data);
          } else if (evtType === 'unread_count_update' && onUnreadUpdate) {
            onUnreadUpdate(data);
          } else if (evtType === 'trend_report_started' && onTrendReportStarted) {
            onTrendReportStarted(data);
          } else if (evtType === 'trend_report_ready' && onTrendReportReady) {
            onTrendReportReady(data);
          } else if (evtType === 'trend_report_failed' && onTrendReportFailed) {
            onTrendReportFailed(data);
          // Business events
          } else if (evtType === 'new_subscriber' && onNewSubscriber) {
            onNewSubscriber(data);
          } else if (evtType === 'batch_updated' && onBatchUpdated) {
            onBatchUpdated(data);
          } else if (evtType === 'service_updated' && onServiceUpdated) {
            onServiceUpdated(data);
          } else if (evtType === 'staff_updated' && onStaffUpdated) {
            onStaffUpdated(data);
          }

        } catch (err) {
          // Malformed message — silently ignore
        }
      };

      ws.onerror = () => {
        isConnectingRef.current = false;
      };

      ws.onclose = () => {
        isConnectingRef.current = false;
        wsRef.current = null;
        // Schedule automatic reconnect after 3 seconds
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, 3000);
        }
      };
    } catch (err) {
      isConnectingRef.current = false;
    }
  }, [
    role,
    onNewMessage,
    onTyping,
    onReadReceipt,
    onUnreadUpdate,
    onTrendReportStarted,
    onTrendReportReady,
    onTrendReportFailed,
    onNewSubscriber,
    onBatchUpdated,
    onServiceUpdated,
    onStaffUpdated,
  ]);

  // Initial connection
  useEffect(() => {
    connect();

    // Reconnect on window focus (browser may have throttled the WS in background)
    const handleFocus = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        connect();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      // Clean up on unmount
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        // Close cleanly — the onclose handler will NOT auto-reconnect since we're unmounting
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // --- Throttled typing indicator ---
  const typingThrottleRef = useRef(null);

  const sendTyping = useCallback((conversationId, isTyping) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // Throttle to one event per 2 seconds
    if (typingThrottleRef.current) return;

    wsRef.current.send(
      JSON.stringify({
        event: 'typing',
        data: { conversation_id: conversationId, is_typing: isTyping },
      })
    );

    typingThrottleRef.current = setTimeout(() => {
      typingThrottleRef.current = null;
    }, 2000);
  }, []);

  return { sendTyping, wsRef };
};

export default useMessagingWS;
